import { useId, useRef, useState } from 'react'
import { FIELD_DRAG_TYPE, type ItemView } from './DataPanel.js'
import { ExpressionAutocomplete, applySuggestion, type Suggestion } from './ExpressionAutocomplete.js'
import { effectiveValue, visibleParameters, type NodeDefinitionView, type NodeParameterView } from './definitions.js'

export interface CredentialChoice {
  id: string
  name: string
  type: string
}

export interface InstanceChoice {
  id: string
  name: string
  /** Канал номера. По нему список сужается под узел (FR-NODE-011). */
  provider: string
}

/** Сценарий, который можно вызвать подсценарием. */
export interface WorkflowChoice {
  id: string
  name: string
}

export interface ParameterPanelProps {
  definition: NodeDefinitionView
  values: Readonly<Record<string, unknown>>
  onChange: (name: string, value: unknown) => void
  /** Инстансы канала для параметров типа `instance`. */
  instances?: readonly InstanceChoice[]
  /**
   * Сценарии арендатора для параметров типа `workflow` — нода подсценария.
   * Выбор из списка, а не ввод идентификатора: идентификатор человек
   * знает разве что из адресной строки (FR-PIPE-021).
   */
  workflows?: readonly WorkflowChoice[]
  /** Credentials арендатора; отбираются по `credentialTypes` описания. */
  credentials?: readonly CredentialChoice[]
  /**
   * Элементы последнего прогона: из них подсказываются поля в выражениях
   * (FR-UI-003). Без прогона список пуст — и это честно, поля берутся
   * только из настоящих данных.
   */
  items?: readonly ItemView[]
  /** Имена нод графа: к ним обращается `$node["Имя"]`. */
  nodeNames?: readonly string[]
  /**
   * Сценарий, чей узел настраивается. Нужен адресу вебхука: без него
   * автор знает путь, но не знает, куда его слать (FR-PIPE-033).
   */
  pipelineId?: string
}

/**
 * Строка целиком из `{{ }}` отдаёт движку значение как есть — так параметру
 * типа `json` можно передать объект выражением. Правило то же, что в
 * `resolveValue` движка: разойдись они, форма приняла бы то, что прогон
 * отвергнет.
 */
const WHOLE_EXPRESSION = /^\s*\{\{[\s\S]*\}\}\s*$/

/** Поля, куда осмысленно переносить поле данных. */
const ACCEPTS_EXPRESSION: ReadonlySet<string> = new Set(['string', 'number', 'json', 'dateTime', 'collection'])

function asText(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2) ?? ''
}

/** Что показать в поле JSON: набранный текст, пока он есть, иначе значение. */
function jsonText(draft: string | undefined, value: unknown): string {
  if (draft !== undefined) return draft
  if (typeof value === 'string') return value
  if (value === undefined) return ''
  return JSON.stringify(value, null, 2) ?? ''
}

/**
 * Панель параметров. Форма собирается по описанию ноды: ни одного имени
 * ноды и ни одного её поля здесь нет (FR-NODE-001).
 *
 * Поля из панели данных переносятся сюда мышью (FR-UI-002): набирать
 * `{{ $json.from }}` руками значит ошибаться в имени поля, которое видно
 * на экране рядом.
 */
export function ParameterPanel({
  definition,
  values,
  onChange,
  instances = [],
  workflows = [],
  credentials = [],
  items = [],
  nodeNames = [],
  pipelineId,
}: ParameterPanelProps): React.JSX.Element {
  const base = useId()
  /**
   * Поле, в котором стоит курсор, и его место. Подсказки показываются
   * только под ним: список под каждым полем закрыл бы форму целиком, а
   * без места курсора нельзя понять, что человек набирает — выражение
   * или обычный текст.
   */
  const [caretAt, setCaretAt] = useState<{ name: string; caret: number } | null>(null)
  const fields = useRef(new Map<string, HTMLInputElement | HTMLTextAreaElement>())
  // Набранный, но ещё не разобравшийся JSON. Держится отдельно от значения:
  // иначе первая же открытая скобка стирала бы то, что человек набрал.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({})

  const parameters = visibleParameters(definition, values)

  const idOf = (parameter: NodeParameterView): string => `${base}-${parameter.name}`

  const acceptDrop = (parameter: NodeParameterView) => (event: React.DragEvent): void => {
    if (!ACCEPTS_EXPRESSION.has(parameter.type)) return
    const dropped = event.dataTransfer.getData(FIELD_DRAG_TYPE)
    if (dropped === '') return
    event.preventDefault()

    if (parameter.type === 'json' || parameter.type === 'collection') {
      // Выражение целиком заменяет значение: дописать его в объект нельзя,
      // не разобрав то, что там уже набрано.
      setDrafts((current) => ({ ...current, [parameter.name]: dropped }))
      setJsonErrors((current) => withoutKey(current, parameter.name))
      onChange(parameter.name, dropped)
      return
    }

    // Дописывается в конец: где стоит курсор, панель не знает, а затирать
    // набранное молча — потеря работы.
    const existing = asText(effectiveValue(parameter, values))
    onChange(parameter.name, existing === '' ? dropped : `${existing}${dropped}`)
  }

  const changeJson = (parameter: NodeParameterView, text: string): void => {
    setDrafts((current) => ({ ...current, [parameter.name]: text }))

    if (text.trim() === '') {
      setJsonErrors((current) => withoutKey(current, parameter.name))
      onChange(parameter.name, undefined)
      return
    }
    if (WHOLE_EXPRESSION.test(text)) {
      setJsonErrors((current) => withoutKey(current, parameter.name))
      onChange(parameter.name, text)
      return
    }

    try {
      const parsed: unknown = JSON.parse(text)
      setJsonErrors((current) => withoutKey(current, parameter.name))
      onChange(parameter.name, parsed)
    } catch {
      // Значение не меняется, пока текст не разобрался: недоделанный JSON,
      // записанный в граф, отклонил бы уже прогон — и отказ пришёл бы
      // много позже правки.
      setJsonErrors((current) => ({ ...current, [parameter.name]: 'Не разбирается как JSON.' }))
    }
  }


  /**
   * Запоминает поле и место курсора. Вешается на всё, что курсор двигает:
   * ввод, щелчок, стрелки. Без `selectionStart` подсказки пришлось бы
   * привязывать к концу строки — и они не работали бы при правке
   * выражения в середине текста.
   */
  const trackCaret = (parameter: NodeParameterView) => (
    event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const field = event.currentTarget
    fields.current.set(parameter.name, field)
    setCaretAt({ name: parameter.name, caret: field.selectionStart ?? field.value.length })
  }

  /** Вставляет подсказку и возвращает курсор за неё. */
  const pickSuggestion = (
    parameter: NodeParameterView,
    text: string,
    caret: number,
    suggestion: Suggestion,
  ): void => {
    const next = applySuggestion(text, caret, suggestion)
    if (parameter.type === 'json' || parameter.type === 'collection') {
      changeJson(parameter, next.value)
    } else {
      onChange(parameter.name, next.value)
    }
    setCaretAt({ name: parameter.name, caret: next.caret })

    // Курсор возвращается в поле: иначе следующий символ уходит в никуда,
    // а человек продолжает набирать выражение.
    const field = fields.current.get(parameter.name)
    if (field !== undefined) {
      window.requestAnimationFrame(() => {
        field.focus()
        field.setSelectionRange(next.caret, next.caret)
      })
    }
  }

  /** Подсказки под полем, если курсор стоит внутри выражения. */
  const suggestionsFor = (parameter: NodeParameterView, text: string): React.ReactNode => {
    if (!ACCEPTS_EXPRESSION.has(parameter.type)) return null
    if (caretAt === null || caretAt.name !== parameter.name) return null
    return (
      <ExpressionAutocomplete
        value={text}
        caret={caretAt.caret}
        items={items}
        nodeNames={nodeNames}
        onPickSuggestion={(suggestion) => {
          pickSuggestion(parameter, text, caretAt.caret, suggestion)
        }}
      />
    )
  }

  return (
    <aside className="панель-параметров" aria-label={`Параметры: ${definition.displayName}`}>
      <h3>{definition.displayName}</h3>

      {parameters.length === 0 ? (
        <p className="панель-параметров__пусто">У этой ноды нет настроек — она работает как есть.</p>
      ) : null}

      {parameters.map((parameter) => {
        const id = idOf(parameter)
        const value = effectiveValue(parameter, values)
        const error = jsonErrors[parameter.name]

        return (
          <div
            key={parameter.name}
            className="параметр"
            onDragOver={(event) => {
              if (ACCEPTS_EXPRESSION.has(parameter.type)) event.preventDefault()
            }}
            onDrop={acceptDrop(parameter)}
          >
            <label htmlFor={id}>
              {parameter.displayName}
              {parameter.required === true ? <span aria-hidden="true"> *</span> : null}
            </label>

            {parameter.type === 'options' ? (
              <select
                id={id}
                value={asText(value)}
                onChange={(event) => {
                  onChange(parameter.name, optionValue(parameter, event.target.value))
                }}
              >
                <option value="">— не выбрано —</option>
                {(parameter.options ?? []).map((option) => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : parameter.type === 'multiOptions' ? (
              <select
                id={id}
                multiple
                value={(Array.isArray(value) ? value : []).map((entry) => String(entry))}
                onChange={(event) => {
                  const chosen = [...event.target.selectedOptions].map((option) =>
                    optionValue(parameter, option.value),
                  )
                  onChange(parameter.name, chosen)
                }}
              >
                {(parameter.options ?? []).map((option) => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : parameter.type === 'boolean' ? (
              <input
                id={id}
                type="checkbox"
                checked={value === true}
                onChange={(event) => {
                  onChange(parameter.name, event.target.checked)
                }}
              />
            ) : parameter.type === 'number' ? (
              <>
                <input
                  id={id}
                  type="text"
                  inputMode="decimal"
                  value={asText(value)}
                  placeholder={parameter.placeholder}
                  onChange={(event) => {
                    onChange(parameter.name, numberOrExpression(event.target.value))
                    trackCaret(parameter)(event)
                  }}
                  onSelect={trackCaret(parameter)}
                  onFocus={trackCaret(parameter)}
                />
                {suggestionsFor(parameter, asText(value))}
              </>
            ) : parameter.type === 'json' || parameter.type === 'collection' ? (
              <>
                <textarea
                  id={id}
                  rows={5}
                  value={jsonText(drafts[parameter.name], value)}
                  placeholder={parameter.placeholder}
                  aria-invalid={error !== undefined}
                  onChange={(event) => {
                    changeJson(parameter, event.target.value)
                    trackCaret(parameter)(event)
                  }}
                  onSelect={trackCaret(parameter)}
                  onFocus={trackCaret(parameter)}
                />
                {suggestionsFor(parameter, jsonText(drafts[parameter.name], value))}
              </>
            ) : parameter.type === 'instance' ? (
              <select
                id={id}
                value={asText(value)}
                onChange={(event) => {
                  onChange(parameter.name, blankToUndefined(event.target.value))
                }}
              >
                <option value="">— не выбрано —</option>
                {/* Список сужен по каналу узла: номер другого канала
                    выбрать можно, но опубликовать нельзя (FR-PIPE-042),
                    и предлагать его — вести человека в тупик. */}
                {instances
                  .filter(
                    (instance) =>
                      definition.channelProvider === undefined ||
                      instance.provider === definition.channelProvider,
                  )
                  .map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name}
                    </option>
                  ))}
              </select>
            ) : parameter.type === 'workflow' ? (
              <select
                id={id}
                value={asText(value)}
                onChange={(event) => {
                  onChange(parameter.name, blankToUndefined(event.target.value))
                }}
              >
                <option value="">— не выбрано —</option>
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </option>
                ))}
              </select>
            ) : parameter.type === 'credentials' ? (
              <select
                id={id}
                value={asText(value)}
                onChange={(event) => {
                  onChange(parameter.name, blankToUndefined(event.target.value))
                }}
              >
                <option value="">— не выбрано —</option>
                {credentials
                  .filter((credential) => (definition.credentialTypes ?? []).includes(credential.type))
                  .map((credential) => (
                    <option key={credential.id} value={credential.id}>
                      {credential.name}
                    </option>
                  ))}
              </select>
            ) : (
              // Дата вводится текстом, а не `datetime-local`: в поле должно
              // помещаться и выражение, а браузерный ввод даты его не примет.
              <input
                id={id}
                type="text"
                value={asText(value)}
                placeholder={parameter.placeholder}
                onChange={(event) => {
                  onChange(parameter.name, event.target.value)
                  trackCaret(parameter)(event)
                }}
                onSelect={trackCaret(parameter)}
                onFocus={trackCaret(parameter)}
              />
            )}

            {parameter.type === 'string' || parameter.type === 'dateTime'
              ? suggestionsFor(parameter, asText(value))
              : null}

            {parameter.description !== undefined ? (
              <p className="параметр__подсказка">{parameter.description}</p>
            ) : null}

            {/* Адрес вебхука целиком: путь автор знает, а куда его слать —
                нет. Показывается прямо под полем пути, чтобы можно было
                скопировать и отдать чужой системе (FR-PIPE-033). */}
            {parameter.name === 'path' && definition.type.includes('webhook') && pipelineId !== undefined ? (
              <p className="параметр__подсказка">
                Адрес для вызова:{' '}
                <code>{`POST ${hookBase()}/api/v2/hooks/${pipelineId}/${asText(value)}`}</code>
              </p>
            ) : null}
            {error !== undefined ? (
              <p className="параметр__ошибка" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        )
      })}
    </aside>
  )
}

/**
 * Начало адреса вебхука. Берётся из адреса открытой страницы: портал и API
 * живут на одном имени, а зашитый адрес разошёлся бы с установкой.
 */
function hookBase(): string {
  return typeof window === 'undefined' ? '' : window.location.origin
}

function withoutKey(source: Record<string, string>, key: string): Record<string, string> {
  const next = { ...source }
  delete next[key]
  return next
}

function blankToUndefined(value: string): string | undefined {
  return value === '' ? undefined : value
}

/** Значение варианта в его исходном типе: `options` бывают числами и флагами. */
function optionValue(parameter: NodeParameterView, raw: string): unknown {
  if (raw === '') return undefined
  const option = (parameter.options ?? []).find((candidate) => String(candidate.value) === raw)
  return option === undefined ? raw : option.value
}

/**
 * Число остаётся числом, а выражение — строкой: движок сохраняет тип
 * значения, и записать `{{ $json.count }}` как NaN значит потерять его.
 */
function numberOrExpression(raw: string): unknown {
  if (raw.trim() === '') return undefined
  if (raw.includes('{{')) return raw
  const parsed = Number(raw)
  return Number.isNaN(parsed) ? raw : parsed
}
